import { useCallback, useEffect, useState } from "react";
import { brandApi } from "./brand-api";

// PR #67 — per-brand global API exposure toggles (cm / wtb / ad_spend), backed
// by brand.api_exposure (tenant-keyed). GET seeds the switches; PATCH persists a
// single toggle immediately. Shared by the onboarding exposure panel and the WTB
// upload's single "expose in APIs" switch.

export type ExposureKey = "expose_cm" | "expose_wtb" | "expose_ad_spend" | "expose_address_book";

export interface Exposure {
  expose_cm: boolean;
  expose_wtb: boolean;
  expose_ad_spend: boolean;
  expose_address_book: boolean;
  updated_at: string | null;
}

function normalize(r: Record<string, unknown>): Exposure {
  return {
    expose_cm: !!r.expose_cm,
    expose_wtb: !!r.expose_wtb,
    expose_ad_spend: !!r.expose_ad_spend,
    expose_address_book: !!r.expose_address_book,
    updated_at: (r.updated_at as string | null) ?? null,
  };
}

export function useExposure(slug: string) {
  const [exposure, setExposure] = useState<Exposure | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await brandApi.get(`/api/brand/${encodeURIComponent(slug)}/exposure`);
      setExposure(normalize(r));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    reload();
  }, [reload]);

  const setKey = useCallback(
    async (key: ExposureKey, value: boolean): Promise<Exposure> => {
      const r = await brandApi.patch(`/api/brand/${encodeURIComponent(slug)}/exposure`, { [key]: value });
      const next = normalize(r);
      setExposure(next);
      return next;
    },
    [slug]
  );

  return { exposure, loading, error, reload, setKey };
}
